import { getNames, registerLocale } from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import ruLocale from 'i18n-iso-countries/langs/ru.json';
import hyLocale from 'i18n-iso-countries/langs/hy.json';

/** One ISO 3166-1 alpha-2 country, ready for an `nz-option`. */
export interface CountryOption {
  /** Alpha-2 code, e.g. `AM` — this is what gets stored. */
  value: string;
  label: string;
}

let localesRegistered = false;

function ensureLocales(): void {
  if (localesRegistered) return;
  registerLocale(enLocale as Parameters<typeof registerLocale>[0]);
  registerLocale(ruLocale as Parameters<typeof registerLocale>[0]);
  registerLocale(hyLocale as Parameters<typeof registerLocale>[0]);
  localesRegistered = true;
}

const cache = new Map<string, CountryOption[]>();

/**
 * Every ISO 3166-1 country, sorted by localized name.
 *
 * Same source as `CountryNamePipe`, so anything selected here renders through
 * that pipe in the tables. The result is cached per language: the list is a few
 * hundred entries and never changes at runtime, so callers can read it straight
 * from a template binding without rebuilding it on every change detection pass.
 */
export function getCountryOptions(lang = 'en'): CountryOption[] {
  const cached = cache.get(lang);
  if (cached) return cached;

  ensureLocales();
  const options = Object.entries(getNames(lang, { select: 'official' }))
    .map(([value, label]) => ({ value, label: String(label) }))
    .sort((a, b) => a.label.localeCompare(b.label, lang));

  cache.set(lang, options);
  return options;
}
